import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { GenericSelect } from "@shared/components/Select";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { useUtilityPlans, useReceiptPreview, useReceiptDownload, useReceiptPrint } from "../../hooks";
import { Plan } from "../../types";

export default function BillTemplateDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { cardType?: string; name?: string; description?: string } || {};
  const [selectedPlanId, setSelectedPlanId] = useState<number | string>("");

  const isMaster = state.cardType?.toLowerCase() === 'master';

  const { data: plansData, isPending: isLoadingPlans } = useUtilityPlans();
  const plans: Plan[] = plansData?.result || [];
  
  const planOptions = plans.map(plan => ({
    label: plan.planName,
    value: String(plan.id)
  }));
  const { id: templateId } = useParams<{id: string}>();
  // Default select first plan
  useEffect(() => {
    if (!isMaster && plans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(plans[0].id);
    }
  }, [plans, selectedPlanId, isMaster]);

  const { data: previewData, isPending: isLoadingPreview } = useReceiptPreview(templateId, selectedPlanId, state.cardType);

  const downloadReceipt = useReceiptDownload();

  const handleDownloadClick = async () => {
    if (!templateId) return;
    try {
      await downloadReceipt.mutateAsync({
        templateId,
        planId: selectedPlanId,
        cardType: state.cardType,
        filename: `receipt-${templateId}-${selectedPlanId}.pdf`,
      });
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const printReceipt = useReceiptPrint();

  const handlePrintClick = async () => {
    if (!templateId) return;
    try {
      await printReceipt.mutateAsync({
        templateId,
        planId: selectedPlanId,
        cardType: state.cardType,
      });
    } catch (error) {
      console.error("Print failed:", error);
    }
  };

  return (
    <div>
      <div className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold leading-tight">{state.name || "Template Detail"}</h1>
            <p className="text-sm text-gray-500">{state.description || "Preview"}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {!isMaster && (
            <>
              <div className="flex items-center gap-2">
                <label htmlFor="plan-select" className="text-sm font-medium text-gray-700">
                  Plan:
                </label>
                <GenericSelect
                  value={selectedPlanId.toString()}
                  onChange={(value) => setSelectedPlanId(value)}
                  disabled={isLoadingPlans}
                  options={planOptions}
                  placeholder={isLoadingPlans ? "Loading plans..." : "Select a plan"}
                  className="w-[200px]"
                />
              </div>
              <div className="h-6 w-px bg-gray-200 mx-2"></div>
            </>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 text-gray-700" 
            onClick={handleDownloadClick}
            isLoading={downloadReceipt.isPending}
          >
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2 text-gray-700" 
            onClick={handlePrintClick}
            isLoading={printReceipt.isPending}
          >
            <Printer className="h-4 w-4" />
            Print
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 p-6 flex justify-center overflow-auto">
        <div className="bg-white shadow-lg rounded border border-gray-200 w-full max-w-[1000px] min-h-[800px]">
          {isLoadingPreview ? (
            <div className="flex items-center justify-center h-full text-gray-500 animate-pulse">
              Loading preview...
            </div>
          ) : previewData ? (
            <iframe
              title="Receipt Preview"
              srcDoc={previewData}
              className="w-full h-full border-0 rounded"
              sandbox="allow-same-origin allow-scripts"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              No preview available for the selected plan.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
